// #390: put the day, month and year of a NUMERIC date in the order the athlete
// chose, in ANY UI language. The separators stay the locale's: only the three
// digit fields swap places. A date with a month name is not reordered.

export type DateOrder = "dmy" | "mdy" | "ymd";

// BCP-47 private-use subtags ("it-x-mdy", "en-US-x-h24"): Intl ignores them,
// so they travel wherever a locale string already travels and dateFormatter
// reads them back. The order is the athlete's; the clock (h12/h24) is the
// device's, kept when the order borrows another locale.
const MARK = /-x-((?:dmy|mdy|ymd|h12|h24)(?:-(?:dmy|mdy|ymd|h12|h24))*)$/;

export function markLocale(locale: string, order: DateOrder | null, hour12: boolean | null): string {
  const tags = [order, hour12 === null ? null : hour12 ? "h12" : "h24"].filter(Boolean);
  return tags.length ? `${locale}-x-${tags.join("-")}` : locale;
}

export function splitDateOrder(locale: string): {
  locale: string;
  order: DateOrder | null;
  hour12: boolean | null;
} {
  const m = MARK.exec(locale);
  if (!m) return { locale, order: null, hour12: null };
  const tags = (m[1] ?? "").split("-");
  const order = (tags.find((t) => t[0] !== "h") as DateOrder | undefined) ?? null;
  const hour12 = tags.includes("h12") ? true : tags.includes("h24") ? false : null;
  return { locale: locale.slice(0, m.index), order, hour12 };
}

const FIELDS = new Set(["day", "month", "year"]);
const KEY = { d: "day", m: "month", y: "year" } as const;

export function reorderDateParts(parts: Intl.DateTimeFormatPart[], order: DateOrder): string {
  const idx: number[] = [];
  parts.forEach((p, i) => {
    if (FIELDS.has(p.type)) idx.push(i);
  });
  if (idx.length < 2) return parts.map((p) => p.value).join("");
  const present = new Set(idx.map((i) => parts[i]?.type));
  const wanted = [...order].map((c) => KEY[c as keyof typeof KEY]).filter((k) => present.has(k));
  const byType = new Map(idx.map((i) => [parts[i]?.type, parts[i]] as const));
  const out = parts.slice();
  idx.forEach((slot, n) => {
    const next = byType.get(wanted[n]);
    if (next) out[slot] = next;
  });
  return out.map((p) => p.value).join("");
}
